import React from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { getGlassPanelSurfaceStyles } from '../helpers/sectionCardStyles'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import type { ProfileCardWithIndicatorsProps } from './profileIndicatorTypes'
import { ProfileIndicatorRow } from './ProfileIndicatorRow'
import { ProfileSplitTitle } from './ProfileSplitTitle'

export type { ProfileCardWithIndicatorsProps, ProfileIndicator } from './profileIndicatorTypes'

/**
 * ProfileCardWithIndicators — profile title, description, and strength indicator rows with optional tooltips.
 */
export const ProfileCardWithIndicators: React.FC<ProfileCardWithIndicatorsProps> = ({
  profileName,
  description,
  indicators
}) => {
  const { t } = useTranslation()
  const helpAriaLabel = t('gamer.feedback.profile.profileIndicators.moreInformationAriaLabel', {
    defaultValue: 'More information about this strength metric'
  })

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        p: { xs: 2, sm: 2.5, md: 3.75 },
        gap: { xs: 1, sm: 1.25, md: 1.5 },
        width: '100%',
        height: '100%',
        alignSelf: 'stretch',
        ...getGlassPanelSurfaceStyles('individual'),
        flex: 'none',
        order: 0,
        flexGrow: 1,
        minWidth: 0
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '0px',
          gap: { xs: 1, sm: 1, md: 1 },
          width: '100%',
          flex: 'none',
          order: 0,
          alignSelf: 'stretch',
          flexGrow: 0,
          minWidth: 0
        }}
      >
        <ProfileSplitTitle profileName={profileName} />
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.body2,
            fontSize: { xs: '13px', sm: '13px', md: '14px' },
            lineHeight: { xs: '18px', sm: '19px', md: '20px' },
            color: INTERAX_COLORS.thunderGrey,
            width: '100%',
            margin: 0,
            minWidth: 0,
            overflowWrap: 'break-word'
          }}
        >
          {description}
        </Typography>
      </Box>

      {indicators.map((indicator, index) => (
        <ProfileIndicatorRow
          key={`${indicator.label}-${indicator.status}-${index}`}
          indicator={indicator}
          helpAriaLabel={helpAriaLabel}
          rowOrder={index + 1}
        />
      ))}
    </Box>
  )
}

export default ProfileCardWithIndicators
